import { useMemo, useEffect, useRef, useCallback } from "react";
import "survey-core/defaultV2.min.css";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";

import customTheme from "../../Incidents/IncidentForm/survey_theme.json";
import useRegexMatch from "../../../hooks/useRegexMatch";
import {
  extractProperties,
  getNewProperties,
} from "../../../utils/commonMethods";
import { baseUrl } from "../../../utils/commonMethods";
import { setCredentials, logOut } from "../../../features/auth/authSlice";
import { useDispatch } from "react-redux";

function DataForm({
  page = "",
  data,
  handleSave,
  formData = null,
  handleTrigger = () => {},
  handlePanelChange = () => {},
  handleAfterPanelRender = () => {},
  readOnly = false,
  assignedToVariables = [],
}) {
  useRegexMatch();

  const formStateRef = useRef({ formData, page });
  const activeRequests = useRef(new Set());
  const assignedToVariablesRef = useRef(assignedToVariables);
  const dispatch = useDispatch();

  useEffect(() => {
    assignedToVariablesRef.current = assignedToVariables;
  });

  useEffect(() => {
    formStateRef.current = { formData, page };
  }, [formData, page]);

  // Refs for event handlers
  const handlersRef = useRef({
    handleTrigger,
    handlePanelChange,
    handleAfterPanelRender,
    handleSave,
    dispatch,
  });

  // Update handler refs
  useEffect(() => {
    handlersRef.current = {
      handleTrigger,
      handlePanelChange,
      handleAfterPanelRender,
      handleSave,
      dispatch,
    };
  }, [
    handleTrigger,
    handlePanelChange,
    handleAfterPanelRender,
    handleSave,
    dispatch,
  ]);

  const sendRequest = useCallback(
    (url, options, callback) => {
      const dispatch = handlersRef.current?.dispatch;
      const xhr = new XMLHttpRequest();
      const requestId = Symbol();

      activeRequests.current.add(requestId);

      xhr.open("GET", url);
      xhr.setRequestHeader("Accept", "application/json");
      xhr.withCredentials = true;

      xhr.onload = () => {
        activeRequests.current.delete(requestId);
        if (xhr.status === 200) {
          callback(JSON.parse(xhr.response));
        } else if (xhr.status === 401) {
          handleTokenRefresh(() => {
            const retryXhr = new XMLHttpRequest();
            retryXhr.open("GET", url);
            retryXhr.setRequestHeader("Accept", "application/json");
            retryXhr.withCredentials = true;

            retryXhr.onload = () => {
              if (retryXhr.status === 200) {
                callback(JSON.parse(retryXhr.response));
              } else {
                console.error("Retry failed:", retryXhr.status);
                if (options?.setItems) options.setItems([], 0);
              }
            };

            retryXhr.onerror = () => {
              console.error("Retry network error");
              if (options?.setItems) options.setItems([], 0);
            };

            retryXhr.send();
          }, dispatch);
        } else {
          if (options?.setItems) options.setItems([], 0);
        }
      };

      xhr.onerror = () => {
        activeRequests.current.delete(requestId);
        if (options?.setItems) options.setItems([], 0);
      };

      xhr.send();
    },
    [handlersRef, activeRequests]
  );

  const handleTokenRefresh = (retryCallback, dispatch) => {
    const refreshXhr = new XMLHttpRequest();
    refreshXhr.open("POST", `${baseUrl}/auth/tokens:refresh`);
    refreshXhr.withCredentials = true;
    refreshXhr.setRequestHeader("Accept", "application/json");

    refreshXhr.onload = () => {
      if (refreshXhr.status === 200) {
        const result = JSON.parse(refreshXhr.response);
        if (result?.user) {
          dispatch(
            setCredentials({
              isAuthenticatedUser: true,
              user: result.user,
            })
          );
          retryCallback();
        } else {
          dispatch(logOut());
        }
      } else {
        dispatch(logOut());
      }
    };

    refreshXhr.send();
  };

  const survey = useMemo(() => {
    const s = new Model(data);
    s.applyTheme(customTheme);
    s.onDynamicPanelItemValueChanged.add((sender, options) => {
      handlersRef.current.handlePanelChange(sender, options);
    });

    s.onValueChanged.add((sender, options) => {
      const dispatch = handlersRef.current?.dispatch;

      if (options.name === "Incident_Date") {
        const subscribedQuestion = s.getQuestionByName(
          "Incident_CSTReportDate"
        );
        subscribedQuestion.value = options.value;
      }

      if (options.name === "Incident_CostCentre") {
        const costCenterKey = options.value;
        if (!costCenterKey) return;

        sendRequestForCostCenter(costCenterKey, sender, dispatch);
      }
      handlersRef.current.handleTrigger(sender, options);
    });

    function sendRequestForCostCenter(costCenterKey, sender, dispatch) {
      const url = `${baseUrl}/hierarchy/cost-centers/${costCenterKey}`;
      sendRequest(
        url,
        dispatch,
        (data) => {
          const questions = [
            { name: "Incident_L1_Directorate", data: data?.directorate },
            {
              name: "Incident_L2_ReportingStream",
              data: data?.reportingStream,
            },
            { name: "Incident_L3_Department", data: data?.department },
            { name: "Incident_L4_Sub-department", data: data?.subDepartment },
            { name: "Incident_L5_Area", data: data?.area },
          ];

          questions.forEach(({ name, data }) => {
            const q = sender.getQuestionByName(name);
            if (!q) return;

            if (data) {
              q.choices = [
                { value: data.key, text: `${data.key} - ${data.description}` },
              ];
              sender.setValue(name, data.key);
            } else {
              q.choices = [];
              sender.setValue(name, null);
            }
          });
        },
        (status, response) => {
          console.error(
            "Failed to load cost center details:",
            status,
            response
          );
        }
      );
    }

    s.onAfterRenderPanel.add((sender, options) => {
      handlersRef.current.handleAfterPanelRender(sender, options);
    });

    s.onComplete.add(async (sender) => {
      const { formData, page } = formStateRef.current;
      let results;

      if (page === "incidentDetails" && formData) {
        const extractedData = extractProperties(
          sender.data,
          formData.currentFormData
        );
        const newlyAddedData = getNewProperties(
          sender.data,
          formData.defaultValues
        );
        const allData = { ...extractedData, ...newlyAddedData };
        if (!allData.hasOwnProperty("Incident_ExtRegServIndicator")) {
          allData.Incident_ExtRegServIndicator = false;
        }
        results = JSON.stringify(allData);
      } else {
        results = sender.data;
      }

      await handlersRef.current.handleSave(results);
    });

    s.onChoicesLazyLoad.add((_, options) => {
      const dispatch = handlersRef.current?.dispatch;

      if (
        options.question.getType() === "tagbox" &&
        options.question.name === "assignedTo"
      ) {
        const page = options.skip / options.take + 1;
        const url = `${baseUrl}/users/?page=${page}&pageSize=${options.take}&name=${options.filter}`;

        sendRequest(url, dispatch, (data) => {
          const dynamicItems = data.items.map((x) => ({
            id: x.id,
            value: `userId-${x.id}`,
            text: `${x.firstName} ${x.lastName} (${x.email})`,
          }));

          const filteredStaticItems =
            options.skip === 0 ? assignedToVariablesRef.current : [];

          const allItems = [...filteredStaticItems, ...dynamicItems];

          options.setItems(allItems, data.total + filteredStaticItems.length);
        });
      }
      if (
        options.question.getType() === "dropdown" &&
        options.question.name === "Incident_L5_Senior_Incident_Manager"
      ) {
        const page = options.skip / options.take + 1;
        const url = `${baseUrl}/users/?page=${page}&pageSize=${options.take}&name=${options.filter}`;

        sendRequest(url, dispatch, (data) => {
          options.setItems(
            data.items.map((x) => ({
              value: x.id,
              text: `${x.firstName} ${x.lastName} (${x.email})`,
            })),
            data.total
          );
        });
      }
      if (
        options.question.getType() === "dropdown" &&
        options.question.name === "Incident_L5_Local_Incident_Manager"
      ) {
        const page = options.skip / options.take + 1;
        const url = `${baseUrl}/users/?page=${page}&pageSize=${options.take}&name=${options.filter}`;

        sendRequest(url, dispatch, (data) => {
          options.setItems(
            data.items.map((x) => ({
              value: x.id,
              text: `${x.firstName} ${x.lastName} (${x.email})`,
            })),
            data.total
          );
        });
      }
      if (
        options.question.getType() === "dropdown" &&
        options.question.name === "Incident_CostCentre"
      ) {
        const page = options.skip / options.take + 1;
        const url = `${baseUrl}/hierarchy/cost-centers?page=${page}&pageSize=${options.take}&query=${options.filter}`;

        sendRequest(url, dispatch, (data) => {
          options.setItems(
            data.items.map((x) => ({
              value: x.key,
              text: `${x.key} - ${x.description}`,
            })),
            data.total
          );
        });
      }
    });

    s.onGetChoiceDisplayValue.add((sender, options) => {
      const dispatch = handlersRef.current?.dispatch;
      if (
        options.question.getType() === "tagbox" &&
        options.question.name === "assignedTo"
      ) {
        const assignToVariableIds = [];
        const userIds = [];

        for (const value of options?.values || []) {
          const [type, id] = value.split("-");
          if (type === "userId") userIds.push(parseInt(id));
          else assignToVariableIds.push(value);
        }

        const assignedToVariableNames =
          assignedToVariablesRef.current
            ?.filter((x) => assignToVariableIds.includes(x.value))
            ?.map((x) => x.text) || [];

        if (userIds.length > 0) {
          const url = `${baseUrl}/users?ids=${userIds.join("&ids=")}`;
          sendRequest(url, dispatch, (data) => {
            const userNames =
              data?.items
                ?.filter((x) => userIds.includes(x.id))
                ?.map((x) => `${x.firstName} ${x.lastName}`) || [];

            options.setItems([...assignedToVariableNames, ...userNames]);
          });
        } else {
          options.setItems(assignedToVariableNames);
        }
      }
      if (
        options.question.getType() === "dropdown" &&
        (options.question.name === "Incident_L5_Senior_Incident_Manager" ||
          options.question.name === "Incident_L5_Local_Incident_Manager")
      ) {
        const userIds = options?.values;
        const url = `${baseUrl}/users?ids=${userIds.join("&ids=")}`;
        sendRequest(url, dispatch, (data) => {
          const names =
            data?.items?.map(
              (x) => `${x.firstName} ${x.lastName} (${x.email})`
            ) || [];

          options.setItems(names);
        });
      }
      if (
        options.question.getType() === "dropdown" &&
        options.question.name === "Incident_CostCentre"
      ) {
        const keys = options?.values;
        const url = `${baseUrl}/hierarchy/cost-centers/${keys}`;
        sendRequest(url, dispatch, (data) => {
          const costCenter = [`${data.key} - ${data.description}`] || [];
          options.setItems(costCenter);
        });
        sendRequestForCostCenter(keys, sender, dispatch);
      }
    });

    return s;
  }, [data, sendRequest]);

  useEffect(() => {
    if (!survey) return;

    if (survey.state === "completed") {
      survey.clear(true, true);
    }

    if (formData) {
      survey.data =
        page === "incidentDetails"
          ? { ...formData.currentFormData, ...formData.defaultValues }
          : formData;
    }
  }, [survey, formData, page]);

  useEffect(() => {
    if (survey) survey.mode = readOnly ? "display" : "edit";
  }, [survey, readOnly]);

  useEffect(() => {
    const requests = activeRequests.current.clear();
    return () => {
      requests?.clear();
    };
  }, []);

  return <Survey model={survey} />;
}

export default DataForm;
